'use strict';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { ProductFitForm } from './components/ProductFitForm.jsx';
import { QuoteWizard } from './components/QuoteWizard.jsx';
import { getInitialStudyBuilderSelections, getQuoteProduct, isFitComplete } from './studyBuilder.config.js';
import { clearStudyBuilderGuestSession, readStudyBuilderGuestSession, writeStudyBuilderGuestSession } from './studyBuilderGuestSession.js';

const EMPTY_SAVED_QUOTE = { lineItems: [], sampleInfo: {}, contactInfo: {} };

/**
 * @param {{ exitHomePath?: string, enableGuestSessionPersistence?: boolean }} props
 */
export default function StudyBuilderPage({ exitHomePath = '/home', enableGuestSessionPersistence = false }) {
	const history = useHistory();
	const [phase, setPhase] = useState(() =>
		enableGuestSessionPersistence && readStudyBuilderGuestSession()?.phase === 'build' ? 'build' : 'fit'
	);
	const [selections, setSelections] = useState(() => {
		if (enableGuestSessionPersistence) {
			const saved = readStudyBuilderGuestSession()?.selections;
			if (saved && typeof saved === 'object') return { ...getInitialStudyBuilderSelections(), ...saved };
		}
		return getInitialStudyBuilderSelections();
	});
	const quoteProduct = useMemo(() => getQuoteProduct(selections), [selections]);

	const restoreSanitizedRef = useRef(false);
	useEffect(() => {
		if (!enableGuestSessionPersistence || restoreSanitizedRef.current) return;
		restoreSanitizedRef.current = true;
		if (phase === 'build' && (!quoteProduct || !isFitComplete(selections))) {
			clearStudyBuilderGuestSession();
			setPhase('fit');
		}
	}, [enableGuestSessionPersistence, phase, quoteProduct, selections]);

	useEffect(() => {
		if (!enableGuestSessionPersistence) return;
		const id = window.setTimeout(() => {
			writeStudyBuilderGuestSession({
				phase,
				selections,
				...(phase === 'fit' ? { quote: EMPTY_SAVED_QUOTE } : {}),
			});
		}, 400);
		return () => window.clearTimeout(id);
	}, [enableGuestSessionPersistence, phase, selections]);

	const handleTopBack = () => {
		if (phase === 'build') {
			setPhase('fit');
			return;
		}
		history.push(exitHomePath);
	};

	const handleContinueToQuote = () => {
		if (isFitComplete(selections)) setPhase('build');
	};

	return (
		<Box sx={{ background: phase === 'fit' ? '#f8fafc' : Brand.metaboliteWhite, minHeight: 'calc(100vh - 60px)' }}>
			{phase === 'build' && (
				<Box sx={{ backgroundColor: Brand.darkModeBlue, color: Brand.pureWhite }}>
					<Container maxWidth="lg" sx={{ py: 2.5 }}>
						<Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2, fontWeight: 700 }}>
							Study details & quote
						</Typography>
						<Typography variant="h5" component="h1" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: '-0.02em' }}>
							Study builder
						</Typography>
					</Container>
				</Box>
			)}
			{phase === 'build' && (
				<Box
					sx={{
						height: 5,
						background: `linear-gradient(90deg, ${Brand.molecularBlue} 0%, ${Brand.livingPurple} 45%, ${Brand.activatedRed} 100%)`,
					}}
				/>
			)}
			<Container maxWidth={phase === 'fit' ? 'md' : 'lg'} data-testid="study-builder-page" sx={{ py: { xs: 3, sm: 5 } }}>
				<Button
					data-testid="study-builder-back"
					startIcon={<ArrowBack />}
					onClick={handleTopBack}
					sx={{
						mb: 2,
						color: '#64748b',
						fontWeight: 500,
						textTransform: 'none',
						'&:hover': { background: 'rgba(0, 0, 0, 0.04)' },
					}}
				>
					{phase === 'fit' ? 'Back to home' : 'Back to selections'}
				</Button>

				{phase === 'fit' && (
					<ProductFitForm value={selections} onChange={setSelections} onContinue={handleContinueToQuote} />
				)}

				{phase === 'build' && quoteProduct && (
					<QuoteWizard
						fitSelections={selections}
						primaryProduct={quoteProduct}
						enableGuestSessionPersistence={enableGuestSessionPersistence}
						initialQuoteDraft={
							enableGuestSessionPersistence ? readStudyBuilderGuestSession()?.quote ?? null : null
						}
						onBack={() => setPhase('fit')}
					/>
				)}
			</Container>
		</Box>
	);
}
